import { DATADOG_APPLICATION_ID, DATADOG_CLIENT_TOKEN, DATADOG_ENABLED } from './config'
import { datadogLogs } from '@datadog/browser-logs'

/** The Datadog logger methods `doLog` dispatches to by name. */
type LogLevel = 'debug' | 'info' | 'warn' | 'error'

/**
 * Anything callers choose to attach to a log line — in practice objects, strings,
 * `Error`s and `null`. Deliberately `unknown` rather than a union: the value is
 * only ever serialised, never inspected, so narrowing it would buy nothing.
 */
type LogContext = unknown

const isPrimitive = (
  value: unknown,
): value is string | number | boolean | bigint | symbol | null | undefined =>
  value === null || (typeof value !== 'object' && typeof value !== 'function')

/**
 * An error's primitive fields, plus its `cause` by the same rule: `JSON.stringify` renders an
 * `Error` as `{}`. Object fields stay out, since an HTTP error's config and response hold tokens
 * and PHI.
 */
const errorFields = (error: object): Record<string, unknown> => {
  const fields: Record<string, unknown> = {}
  for (const name of Object.getOwnPropertyNames(error)) {
    const field: unknown = Reflect.get(error, name)
    if (isPrimitive(field)) fields[name] = field
    else if (name === 'cause' && typeof field === 'object') fields[name] = errorFields(field)
  }
  return fields
}

class Logger {
  private static instance: Logger

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger()
    }
    return Logger.instance
  }

  isDatadogEnabled() {
    return !!(DATADOG_ENABLED && DATADOG_APPLICATION_ID && DATADOG_CLIENT_TOKEN)
  }

  private contextObjToString(contextObj: LogContext): string {
    if (!contextObj) return ''
    if (contextObj instanceof Error) return JSON.stringify(errorFields(contextObj))
    return JSON.stringify(contextObj, (_key: string, value: unknown) =>
      value === undefined ? null : value,
    )
  }

  private generateLogMessage(
    tag: string | null = null,
    message: string,
    ...contextObjs: LogContext[]
  ) {
    let logMessage = ''
    if (tag) {
      logMessage += tag.toUpperCase() + ' '
    }
    logMessage += message

    if (contextObjs.length) {
      logMessage += ' -- Additional metadata: '
      for (const context of contextObjs) {
        logMessage += this.contextObjToString(context) + '; '
      }
    }
    return logMessage
  }

  doLog = (level: LogLevel, message: string, ...contextObjs: LogContext[]) => {
    const logMessage = this.generateLogMessage(level, message, ...contextObjs)
    // eslint-disable-next-line no-console -- the logger is the one place that writes to the console
    console.log(logMessage)

    if (this.isDatadogEnabled()) datadogLogs.logger[level](logMessage) // Map to the appropriate Datadog API call by log level
  }

  /** Attaches `value` to every Datadog log line under `key`, until it is set to `undefined`. */
  setGlobalContext = (key: string, value: unknown) => {
    if (!this.isDatadogEnabled()) return
    if (value === undefined) datadogLogs.removeGlobalContextProperty(key)
    else datadogLogs.setGlobalContextProperty(key, value)
  }

  debug = (message: string, ...contextObjs: LogContext[]) => {
    this.doLog('debug', message, ...contextObjs)
  }

  info = (message: string, ...contextObjs: LogContext[]) => {
    this.doLog('info', message, ...contextObjs)
  }

  warn = (message: string, ...contextObjs: LogContext[]) => {
    this.doLog('warn', message, ...contextObjs)
  }

  error = (message: string, ...contextObjs: LogContext[]) => {
    this.doLog('error', message, ...contextObjs)
  }
}

export const logger = Logger.getInstance()
